/**
 * V2 source path: customer/midas/components/v2/PlatformModeCardV2.tsx
 * V1 source: customer/midas/components/MidasFleetChips.tsx (platform card section)
 * Strategy: scratch-with-reused-handlers
 * Verified no-MUI: yes
 * Phase: 3 — Round 17 (Alt A Calm restyle)
 *
 * Round 17 — Alt A Calm:
 *   The Round 7 dark-graphite brick is replaced with a calm cream card on the
 *   sidebar's bone background. The card is now visually quiet so the eye is
 *   freed for the machine list. Anchor for the molded palette is preserved
 *   via:
 *     - 3px terracotta left rule (visible without hover, like an active
 *       fleet row)
 *     - Faint terracotta hairline border (alpha 0x2E ≈ 18%)
 *     - Terracotta MODE eyebrow + 6px terracotta dot
 *
 * Layout per Paper artboard 59R-0:
 *   - Sora 11 mono uppercase eyebrow "MODE" + 6px terracotta dot
 *   - Sora 15/600 title "Platform Mode" #2A2824
 *   - Sora 12 caption "General assistant — N active threads" #8C8375
 *
 * The whole card is the click target — copies V1 handlePlatformBodyClick.
 */
import React, { useCallback } from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import {
  setMachineContext,
  startNewChat,
} from "../../../../shared/chatBot/chatBotSlice";
import { setActiveThreadId } from "../../midasPageSlice";

interface PlatformModeCardV2Props {
  /** Optional click hook fired AFTER state has been reset — used by mobile to dismiss the drawer. */
  onAfterClick?: () => void;
}

const PlatformModeCardV2: React.FC<PlatformModeCardV2Props> = ({
  onAfterClick,
}) => {
  const dispatch = useAppDispatch();

  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const platformThreadCount = useAppSelector(
    (s) => s.midasPage.platformThreadCount
  );
  const isPlatformActive = !machineContext;

  // Logic copied from V1 MidasFleetChips.handlePlatformBodyClick
  const handleClick = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    if (!isPlatformActive) {
      dispatch(setMachineContext(null));
    }
    if (onAfterClick) onAfterClick();
  }, [dispatch, isPlatformActive, onAfterClick]);

  const threadCountLabel =
    platformThreadCount === 1
      ? "1 active thread"
      : `${platformThreadCount} active threads`;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Platform Mode — General assistant"
      aria-pressed={isPlatformActive}
      className="group flex w-full min-h-[64px] flex-col gap-1 rounded-[10px] bg-[#FBF6EC] px-4 py-3.5 text-left outline-none transition-colors duration-200 hover:bg-[#F4EFE3] focus-visible:ring-2 focus-visible:ring-midas-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-midas-bone active:scale-[0.99] motion-reduce:active:scale-100 motion-reduce:transition-none"
      style={{
        borderTop: "1px solid rgba(212,87,43,0.18)",
        borderRight: "1px solid rgba(212,87,43,0.18)",
        borderBottom: "1px solid rgba(212,87,43,0.18)",
        borderLeft: "3px solid #D4572B",
      }}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-midas-terracotta"
        />
        <span
          className="font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-midas-terracotta"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          MODE
        </span>
        {isPlatformActive && (
          <span
            aria-hidden="true"
            className="ml-auto h-1.5 w-1.5 rounded-full bg-[#2D8C4E]"
            style={{ boxShadow: "0 0 4px rgba(45,140,78,0.55)" }}
          />
        )}
      </div>
      <div
        className="font-sora text-[15px] font-semibold leading-tight tracking-[-0.01em] text-midas-graphite"
        style={{ fontFamily: "'Sora', sans-serif" }}
      >
        Platform Mode
      </div>
      <div
        className="font-sora text-[12px] font-normal text-midas-muted-stone"
        style={{ fontFamily: "'Sora', sans-serif" }}
      >
        General assistant{" "}
        <span aria-hidden="true">—</span> {threadCountLabel}
      </div>
    </button>
  );
};

export default PlatformModeCardV2;
